import { useState } from 'react';
import WeatherGlyph from './WeatherGlyph';
import WeatherHourStrip from './WeatherHourStrip';
import { hoursFrom, labelFor, nextRain, readingIsCurrent, type WeatherDoc } from '../lib/weather';
import { fmtTime, now } from '../lib/time';

/**
 * What it is doing outside, as a pill on the "Right now" heading (D67).
 *
 * **It was a full-width row and that was too much.** Videet's words were that
 * it "takes the whole span", and on a 390px screen it did: 63px of permanent
 * height for a temperature, sitting between a guest's team card and the event
 * they opened the page to see. The reading is worth a glance, not a card.
 *
 * So: a pill on the heading line, where there was already spare space between
 * "Up next" and the date, and the detail — feels-like, humidity, when the rain
 * comes — one tap away rather than always on screen. Collapsed it is about
 * 70px wide and costs the page no height at all.
 *
 * **One tree at both widths.** `Now` is explicit that nothing on it branches on
 * width in JavaScript and CSS decides, so this is not a phone-only variant: the
 * desktop column gets the same pill. It has more room, but the argument for
 * putting a measurement behind a tap is not about room.
 *
 * **Rendered as two siblings inside `.day-head`**, not as a wrapper around it.
 * The panel carries `flex-basis: 100%` so it wraps onto its own line under the
 * heading, which keeps the heading's own markup in `Now` where it belongs
 * rather than passing a title through this component to be re-emitted.
 */
/** Ties the button to the region it reveals. A constant rather than `useId`
 *  because exactly one of these is ever mounted — `Now` renders it once. */
const PANEL_ID = 'wx-panel';

export default function WeatherNow({ weather }: { weather: WeatherDoc | null }) {
  /* Before the early return — a hook cannot be conditional, and this renders
     nothing whenever there is no usable reading. */
  const [open, setOpen] = useState(false);

  const at = now();
  const current = weather?.current;
  /**
   * No reading, or one too old to call current, and the pill is not there.
   *
   * The second case is the offline one: the cache is served before the network
   * is consulted, so a guest who has been offline since yesterday would
   * otherwise be shown yesterday afternoon's measurement as though it were the
   * weather outside. Saying nothing is correct; saying 24° is not.
   */
  if (!current || !readingIsCurrent(current, at)) return null;

  const rain = nextRain(weather!.hours, at);
  const label = labelFor(current.code);
  const ahead = hoursFrom(weather!.hours, at);

  /**
   * The spoken version of the pill.
   *
   * The visible content is a glyph and a number, which reads as "24 degrees"
   * and nothing else — so the label has to say what the picture means and what
   * the control does. **The rain warning is part of it.** That badge exists
   * precisely so a guest sees it without tapping; leaving it out of the label
   * gave a screen-reader user the temperature and no warning until after they
   * opened the panel, which is the one thing it was added to avoid.
   */
  const spoken = [
    `Weather: ${current.temp} degrees, ${label}`,
    rain && `${rain.rainChance}% chance of rain around ${fmtTime(rain.at)}`,
    open ? 'Hide detail' : 'Show detail',
  ].filter(Boolean).join('. ');

  return (
    <>
      <button
        type="button"
        className="wx-pill"
        aria-expanded={open}
        aria-controls={PANEL_ID}
        aria-label={spoken}
        onClick={() => setOpen((o) => !o)}
      >
        <WeatherGlyph code={current.code} isDay={current.isDay} size={15} />
        <span className="wx-pill-temp">{current.temp}°</span>
        {/* Only when there is something to carry an umbrella for. A standing
            "0%" would be furniture on the dry days and would stop being read
            before the one day it mattered. */}
        {rain && <span className="wx-pill-rain">{rain.rainChance}%</span>}
      </button>

      {open && (
        <div className="wx-panel" id={PANEL_ID}>
          {/* One line of now, then the hours. The summary stays because
              feels-like is the fact a guest acts on — 31° against 39° felt is
              the difference between linen and not — and it is one line rather
              than a row of its own. */}
          <span className="wx-panel-main">
            {label} · feels like {current.feelsLike}° · {current.humidity}% humidity
          </span>
          {/* The forecast rather than a restatement of the pill, and a
              sideways strip rather than a vertical table — same component the
              Schedule's phone strip uses, so the two cannot drift. A twelve-row
              table here would be taller than the event card it opens above.
              Rendered only when there are hours: they run out at the forecast
              horizon, and an empty scroll box says less than no scroll box. */}
          {ahead.length > 0 && <WeatherHourStrip hours={ahead} label="Hourly forecast" />}
        </div>
      )}
    </>
  );
}
